import i18n from 'i18n';
import { COMMON_GRID_COLUMN_FIELD_NAMES } from 'shared/commonGrid/grid-enums';
import { ColGroupDef } from '@ag-grid-enterprise/all-modules';
import {
  DistributorConfiguration,
  DistributorConfigurationDestination,
  DistributorConfigurationListener,
} from 'data/distributorConfiguration/distributorConfiguration.types';
import { stateValueFormatter } from 'utils/valueFormatters';
import { getFieldFromFirstInterfaceNameValueGetter } from 'utils/valueGetters';
import getNumberOfItemsFromFieldNameValueGetter from 'utils/valueGetters/getNumberOfItemsFromFieldNameValueGetter';
import {
  moduleNameAndInstanceIdValueGetter,
} from './utils/distributorValueGetters';
import DistributorStatusCellRenderer from './components/DistribuorStatusCellRenderer';
import distributorValueGetter from './utils/distributorValueGetter';

export enum DistributorMenuActionsType {
  Copy = 'copy',
  Delete = 'delete',
  Edit = 'edit',
  View = 'view'
}

export const distributorColumnDefs: ColGroupDef[] = [
  {
    headerName: '',
    children: [
      {
        headerName: i18n.t('ss8Distributor.grid.configurationName'), // 'Configuration Name',
        field: 'name',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        tooltipField: 'name',
        flex: 1,
        minWidth: 198,
      },
      {
        field: 'moduleName',
        headerName: i18n.t('ss8Distributor.grid.moduleInstanceId'), // 'Module/Instance ID',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 130,
        tooltipValueGetter: moduleNameAndInstanceIdValueGetter,
        valueGetter: moduleNameAndInstanceIdValueGetter,
      },
      {
        headerName: i18n.t('ss8Distributor.grid.afType'), // 'AF Type',
        field: 'afType',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        tooltipField: 'afType',
        flex: 1,
        minWidth: 150,
      },
      {
        headerName: i18n.t('ss8Distributor.grid.currentStatus'), // 'Current Status',
        field: 'currentStatus',
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        tooltipField: 'currentStatus',
        flex: 1,
        minWidth: 140,
        cellRenderer: DistributorStatusCellRenderer as any,
        valueGetter: distributorValueGetter,
      },
      {
        headerName: i18n.t('ss8Distributor.grid.numberListeners'), // '# of Listeners',
        field: 'listeners',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 110,
        tooltipValueGetter: getNumberOfItemsFromFieldNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
        valueGetter: getNumberOfItemsFromFieldNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
      },
      {
        headerName: i18n.t('ss8Distributor.grid.nodeName'), // 'Node Name',
        field: 'nodeName',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        tooltipField: 'nodeName',
        flex: 1,
        minWidth: 150,
        hide: true,
      },
    ],
  },
  {
    headerName: i18n.t('ss8Distributor.grid.listener1'), // 'Listener 1',
    children: [
      {
        headerName: i18n.t('ss8Distributor.grid.type'), // 'Type',
        field: 'afProtocol',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 134,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
      },
      {
        headerName: i18n.t('ss8Distributor.grid.ipAddresses'), // 'IP Addresses',
        field: 'ipAddress',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 158,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
      },
      {
        headerName: i18n.t('ss8Distributor.grid.port'), // 'Port',
        field: 'port',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 100,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener >('listeners'),
      },
      {
        headerName: i18n.t('ss8Distributor.grid.configuredState'), // 'Configured State',
        field: 'requiredState',
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 130,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener>('listeners'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationListener >('listeners'),
        valueFormatter: stateValueFormatter,
      },
    ],
  },
  {
    headerName: i18n.t('ss8Distributor.grid.destination1'), // 'Destination 1',
    children: [
      {
        headerName: i18n.t('ss8Distributor.grid.ipAddresses'), // 'IP Addresses',
        field: 'ipAddress',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 158,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationDestination >('destinations'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationDestination >('destinations'),
      },
      {
        headerName: i18n.t('ss8Distributor.grid.port'), // 'Port',
        field: 'port',
        sortable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 100,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationDestination>('destinations'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationDestination>('destinations'),
      },
      {
        headerName: i18n.t('ss8Distributor.grid.configuredState'), // 'Configured State',
        field: 'requiredState',
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        filterParams: {
          buttons: ['clear', 'apply'],
        },
        resizable: true,
        flex: 1,
        minWidth: 130,
        tooltipValueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationDestination>('destinations'),
        valueGetter: getFieldFromFirstInterfaceNameValueGetter<DistributorConfiguration, DistributorConfigurationDestination >('destinations'),
        valueFormatter: stateValueFormatter,
      },
    ],
  },
];
